/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
/* eslint-disable tailwindcss/no-custom-classname */


import { useEffect, useId, useRef, useState } from 'react';
import { CmsImage } from '../../components/CmsImage';
import { MaterialIcon } from '../../components/MaterialIcon';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TRANSLATIONS } from './i18n';
import { TiProviderHeroProps } from './types';

// Fixed hero background for every TI provider page (CMS-1639) — editors do
// not pick this per-page, so it is not a CMS field.
const STATIC_BACKGROUND_URL =
  'https://mhgprod.blob.core.windows.net/moneyhero/strapi-uploads/m_99562a71_2479_4c9d_bcf9_98eb7290f048_fda06d1d11.png';

export const TiProviderHero = ({
  provider,
  title,
  description,
  promoCode,
  promoCodeEnabled,
}: TiProviderHeroProps): JSX.Element => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  // Per-instance id so two heroes on one page can't collide on the
  // description id / aria-controls relationship.
  const descriptionRegionId = useId();

  const threshold = context.lang.startsWith('zh') ? 120 : 250;
  const [expanded, setExpanded] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  // Hold the reset-timer handle in a ref so rapid re-clicks can clear and
  // restart it. The previous effect keyed on `copied` did not re-run when
  // `setCopied(true)` was called while already `true`, so the 2s window
  // expired from the first click instead of the latest.
  const copyResetRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyResetRef.current !== null) clearTimeout(copyResetRef.current);
    },
    [],
  );

  // Trim once: visibility, display, and clipboard must agree. An editor-typed
  // '  CODE  ' would otherwise be copied with surrounding whitespace that
  // merchant checkouts reject.
  const trimmedPromoCode = promoCode?.trim() ?? '';
  const promoCardVisible = !!trimmedPromoCode && promoCodeEnabled !== false;

  const handleCopy = async (): Promise<void> => {
    // Guard `navigator.clipboard` — insecure context / older browsers leave
    // it undefined. Click becomes a no-op rather than throwing.
    if (
      typeof navigator === 'undefined' ||
      !navigator.clipboard ||
      typeof navigator.clipboard.writeText !== 'function'
    ) {
      return;
    }
    try {
      await navigator.clipboard.writeText(trimmedPromoCode);
    } catch {
      return;
    }
    setCopied(true);
    if (copyResetRef.current !== null) clearTimeout(copyResetRef.current);
    copyResetRef.current = setTimeout(() => {
      setCopied(false);
      copyResetRef.current = null;
    }, 2000);
  };

  // Array.from so 4-byte emoji and CJK-extension chars count as one and never split mid-codepoint.
  const chars = Array.from(description);
  const isOverThreshold = chars.length > threshold;
  const truncated = isOverThreshold
    ? `${chars.slice(0, threshold).join('')}…`
    : null;

  const displayedDescription =
    isOverThreshold && !expanded ? truncated : description;

  return (
    <section
      className="relative -mx-spacing/padding/section-x overflow-hidden md:mx-0 md:rounded-radius/card"
      data-component="ti-provider-hero"
    >
      <CmsImage
        src={STATIC_BACKGROUND_URL}
        alt=""
        aria-hidden
        fill
        loading="eager"
        fetchPriority="high"
        sizes={{ _: '100vw', lg: '1130px' }}
        className="object-cover"
      />
      {/* Darkening overlay per Figma (CMS-1639): vertical black gradient,
          30% at top → 40% at bottom, heavier toward the lower text. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/40"
      />
      <div className="relative flex flex-col items-center gap-spacing/gap/soft-12px px-spacing/padding/section-x py-spacing/gap/section-y-lg text-white md:items-start md:py-spacing/gap/section-y">
        {/* Provider Logo Container (Alpha DS, Figma 13044-49377; CMS-1991 QA):
            a translucent plate so the logo reads on the dark photo. 12px
            padding, 8px radius, white at 20%. `max-w-full` keeps a very wide
            wordmark inside the hero content box. */}
        <div className="max-w-full rounded-radius/card-inner bg-white/20 px-spacing/padding/x-sm py-spacing/padding/y-sm">
          <CmsImage
            src={provider.providerImage}
            alt={provider.providerName}
            width={80}
            height={80}
            // Slot width for srcset selection: with `w-auto` a wide (~2:1) logo
            // renders up to ~2x the fixed height, so request that much. Note the
            // CmsImage scale (`sm` = 768px) is offset from Tailwind's (`md:` =
            // 768px), so `sm` here pairs with the `md:` height class below.
            sizes={{ _: 160, sm: 200 }}
            // CMS-1991: fixed height (80px, 100px from md per design QA), width
            // follows the intrinsic aspect ratio so wide logos are not
            // letterboxed in a square. `width: auto` is inline so next/image
            // does not warn about a modified height without a matching width.
            // `max-w-full` + `object-contain` scale an over-wide logo down
            // inside the fixed-height box instead of overflowing the plate.
            className="h-20 w-auto max-w-full object-contain md:h-25"
            style={{ width: 'auto' }}
          />
        </div>
        {title && (
          <h1 className="mhg-alpha-typography-title-1 text-center text-white md:text-left">
            {title}
          </h1>
        )}
        {description && (
          // Description is desktop-only per CMS-1639 plan / Figma — mobile
          // hides it (the hero card is too narrow to read multi-line copy).
          <div className="hidden md:block">
            <div className="mhg-alpha-typography-body-2-relaxed whitespace-pre-line text-white">
              <span id={descriptionRegionId}>{displayedDescription}</span>
              {isOverThreshold && (
                <>
                  {' '}
                  <button
                    type="button"
                    aria-expanded={expanded}
                    aria-controls={descriptionRegionId}
                    onClick={() => setExpanded((prev) => !prev)}
                    className="underline"
                  >
                    {expanded ? staticText.see_less : staticText.see_more}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        {promoCardVisible && (
          <div
            data-component="ti-provider-hero-promo"
            className="flex items-center gap-spacing/gap/soft-6px rounded-[8px] border border-white/20 bg-white/10 px-[13px] py-[7px] text-[12px] leading-none"
          >
            <span className="font-semibold text-white">
              {staticText.promo_label}
            </span>
            <span className="font-extrabold text-[#ffcb45]">
              {trimmedPromoCode}
            </span>
            <button
              type="button"
              onClick={handleCopy}
              aria-label={copied ? staticText.copied : staticText.copy}
              className="inline-flex shrink-0 items-center justify-center text-text/on-dark"
            >
              <MaterialIcon
                icon={copied ? 'check' : 'content_copy'}
                className="text-[16px] md:text-[18px]"
              />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
